/**
 * Bridge between Server Action error strings and next-intl.
 *
 * Server Actions return error strings to clients via the
 * `{ success: false, error: '...' }` pattern. Two flavours coexist:
 *
 * 1. **Translation keys** (preferred for new code) — strings prefixed with
 *    `errors.`, e.g. `'errors.common.notAuthenticated'`. The convention is
 *    encoded by `isErrorKey` below.
 * 2. **Free text** — forwarded from Zod (`parsed.error.issues[0].message`),
 *    Supabase storage errors, third-party SDK error messages, or legacy
 *    actions that haven't been migrated yet.
 *
 * `translateActionError` translates keys and passes free text through
 * unchanged, so the migration can be incremental without breaking any
 * existing call site.
 *
 * Usage in client components:
 *
 *   const t = useTranslations();
 *   // ...
 *   {state && !state.success && (
 *     <p role="alert">{translateActionError(state.error, t)}</p>
 *   )}
 */

type AnyTranslate = (
  key: string,
  values?: Record<string, string | number | Date>,
) => string;

export function isErrorKey(raw: string): boolean {
  return raw.startsWith('errors.');
}

export function translateActionError(
  raw: string | null | undefined,
  t: AnyTranslate,
  values?: Record<string, string | number | Date>,
): string {
  if (!raw) return '';
  if (!isErrorKey(raw)) return raw;
  // next-intl returns the key path on miss; that's a fine fallback —
  // worst case the user sees something like "errors.foo.bar" instead of a
  // crash, and the missing key shows up in dev console.
  return t(raw, values);
}
